import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import Chart from "react-apexcharts";

const Card = ({
  title,
  percent,
  used,
  total,
  unit,
  accent,
  minSize = 90,
  maxSize = 200,
}) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [autoSize, setAutoSize] = useState<number>(120);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      const w = Math.floor(
        entry.contentBoxSize?.[0]?.inlineSize ?? el.clientWidth
      );
      const gauge = Math.max(minSize, Math.min(maxSize, w - 8));
      setAutoSize(gauge);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [minSize, maxSize]);

  const [animatedPct, setAnimatedPct] = useState(0);
  useEffect(() => {
    let start: number | null = null;
    const dur = 700;
    const from = 0;
    const to = Math.max(0, Math.min(100, percent));
    const tick = (t: number) => {
      if (start === null) start = t;
      const e = Math.min(1, (t - start) / dur);
      const ease = 1 - Math.pow(1 - e, 3);
      setAnimatedPct(Math.round(from + (to - from) * ease));
      if (e < 1) requestAnimationFrame(tick);
    };
    const raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [percent]);

  const sz = autoSize;
  const scale = Math.max(0.45, Math.min(1, sz / 300));
  const cutRatio = sz <= 120 ? 0.2 : 0.27;
  const cut = Math.round(sz * cutRatio);
  const shift = Math.round(cut * 0.58);

  const options = useMemo(() => {
    const valueFont = `${Math.max(12, Math.round(26 * scale))}px`;
    const totalFont = `${Math.max(9, Math.round(11 * scale))}px`;
    const pad = Math.round(-6 * scale);

    return {
      chart: {
        animations: { enabled: true },
        sparkline: { enabled: true },
        toolbar: { show: false },
        foreColor: "#0f172a",
      },
      stroke: { lineCap: "round" },
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "horizontal",
          shadeIntensity: 0.3,
          gradientToColors: [accent],
          inverseColors: false,
          opacityFrom: 0.95,
          opacityTo: 1,
          stops: [0, 30, 60, 100],
          colorStops: [
            { offset: 0, color: accent, opacity: 0.95 },
            { offset: 60, color: accent, opacity: 1 },
            { offset: 100, color: accent, opacity: 1 },
          ],
        },
      },
      colors: [accent],
      plotOptions: {
        radialBar: {
          startAngle: -120,
          endAngle: 120,
          hollow: {
            size: "64%",
            background: "#ffffff",
            margin: 0,
            dropShadow: {
              enabled: true,
              top: 2,
              left: 0,
              blur: Math.max(5, Math.round(8 * scale)),
              opacity: 0.06,
              color: "#0f172a",
            },
          },
          track: {
            background: "#eef2f7",
            strokeWidth: "100%",
            margin: Math.max(1, Math.round(2 * scale)),
            dropShadow: {
              enabled: true,
              blur: Math.max(2, Math.round(3 * scale)),
              opacity: 0.15,
              top: 1,
            },
          },
          dataLabels: {
            show: true,
            name: { show: false },
            value: {
              show: true,
              fontSize: valueFont,
              fontWeight: 900,
              offsetY: Math.round(-2 * scale),
              formatter: () => `${animatedPct}`,
            },
            total: {
              show: !!unit,
              label: unit,
              color: "#64748b",
              fontSize: totalFont,
              fontWeight: 800,
            },
          },
        },
      },
      grid: { padding: { top: 0, bottom: 0, left: pad, right: pad } },
      tooltip: { enabled: false },
    } as ApexCharts.ApexOptions;
  }, [accent, animatedPct, unit, scale]);

  const series = useMemo(() => [animatedPct], [animatedPct]);

  return (
    <Wrap ref={wrapRef} $accent={accent} $size={sz}>
      <TopTitle $size={sz}>{title}</TopTitle>

      <Information>
        <strong>{used?.toLocaleString?.() ?? used}</strong>
        <span>
          / {total?.toLocaleString?.() ?? total} {unit}
        </span>
      </Information>

      <Crop $size={sz} $cut={cut}>
        <ChartStage $size={sz} $shift={shift}>
          <Sweep />
          <Specular className="specular" />
          <Chart
            options={options}
            series={series}
            type="radialBar"
            height={sz}
            width={sz}
          />
        </ChartStage>
      </Crop>
    </Wrap>
  );
};

export default Card;

const rise = keyframes`
  from { transform: translateY(4px); opacity: .0 }
  to   { transform: translateY(0);   opacity: 1 }
`;

const sweep = keyframes`
  0%   { transform: rotate(0turn);   opacity: .28 }
  85%  { opacity: .06 }
  100% { transform: rotate(1turn); opacity: 0 }
`;

const Wrap = styled.section<{ $accent?: string; $size?: number }>`
  --sz: ${({ $size = 300 }) => $size};
  --scale: calc(var(--sz) / 300);
  --accent: ${({ $accent }) => $accent ?? "#22c55e"};
  width: 100%;
  height: 200px;
  display: flex;
  margin: 10;
  flex-direction: column;
  align-items: center;
  justify-items: center;
  background: linear-gradient(180deg, #fff, #f8fafc);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
  animation: ${rise} 0.24s ease both;

  &:hover {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
    border-color: color-mix(in oklab, #e2e8f0 65%, var(--accent));
    .specular {
      opacity: 0.5;
      transform: translateY(-1px);
    }
  }
`;

const TopTitle = styled.h3<{ $size?: number }>`
  --sz: ${({ $size = 300 }) => $size};
  --scale: calc(var(--sz) / 300);
  margin: clamp(8px, calc(6px * var(--scale)), 12px);
  color: #0f172a;
  font-weight: 800;
  font-size: 13px;
  line-height: 1.1;
  text-align: center;
`;

const Crop = styled.div<{ $size: number; $cut: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size, $cut }) => Math.max(0, $size - $cut)}px;
  overflow: hidden;
  display: grid;
  place-items: center;
`;

const ChartStage = styled.div<{ $size: number; $shift: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  display: grid;
  place-items: center;
  margin-top: ${({ $shift }) => -$shift}px;
`;

const Sweep = styled.i`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  pointer-events: none;
  mask: radial-gradient(circle at center, transparent 57%, black 58%);
  background: conic-gradient(
    from 0turn,
    color-mix(in oklab, var(--accent) 26%, transparent) 0%,
    transparent 35% 100%
  );
  animation: ${sweep} 1s ease-out both;
`;

const Specular = styled.i`
  position: absolute;
  inset: 10px;
  border-radius: 50%;
  pointer-events: none;
  box-shadow: inset 0 12px 26px rgba(255, 255, 255, 0.75),
    inset 0 -10px 18px rgba(15, 23, 42, 0.04);
  opacity: 0.25;
  transition: opacity 0.25s ease, transform 0.18s ease;
`;

const Information = styled.div`
  display: flex;
  font-size: clamp(10px, calc(20px * var(--scale)), 18px);
  color: #0f172a;
  font-weight: 800;
  opacity: 0.95;
  line-height: 1.1;

  > strong {
    font-weight: 900;
    display: block;
  }
  > span {
    color: #64748b;
    font-weight: 700;
    display: block;
    margin-top: calc(2px * var(--scale));
  }
`;
